<template>
  <div class="toolbar-wrapper" :class="{ 'collapsed': isCollapsed }">
    <!-- 顶部按钮栏 -->
    <div class="toolbar-header">
      <div class="toolbar-buttons" v-show="!isCollapsed">
        <button class="toolbar-btn" :class="{ 'active': textType === 'general' }" @click="changeTextType('general')">通用文案</button>
        <button class="toolbar-btn" :class="{ 'active': textType === 'ecommerce' }" @click="changeTextType('ecommerce')">电商营销</button>
        <button class="toolbar-btn" :class="{ 'active': textType === 'project' }" @click="changeTextType('project')">项目方案</button>
        <button class="toolbar-btn" :class="{ 'active': textType === 'improve' }" @click="changeTextType('improve')">文案改进</button>
        <button class="toolbar-btn" :class="{ 'active': textType === 'summary' }" @click="changeTextType('summary')">总结报告</button>
      </div>
      <!-- 收缩/展开按钮 -->
      <button class="collapse-btn" @click="toggleCollapse" :title="isCollapsed ? '展开工具栏' : '收缩工具栏'" :class="{ 'expanded': !isCollapsed }">
        <div class="collapse-arrow" :class="{ 'up': isCollapsed }">▼</div>
      </button>
    </div>
    
    <!-- 提示词输入区 -->
    <div class="prompt-input-container">
      <textarea 
        v-model="form.prompt" 
        class="prompt-input" 
        placeholder="描述您想要生成的文案内容..."
        rows="3"
      ></textarea>
    </div>
    
    <!-- 底部参数选项 -->
    <div class="toolbar-options">
      <!-- 模型选择 -->
      <div class="option-group">
        <select v-model="form.model" class="option-select">
          <option v-for="model in textModels" :key="model.identifier" :value="model.identifier">
            {{ model.name }}
            <span v-if="model.is_hot"> (热门)</span>
            <span v-if="model.is_new"> (新品)</span>
          </option>
        </select>
      </div>
      
      <!-- 字数选择 -->
      <div class="option-group">
        <select v-model="form.wordCount" class="option-select">
          <option v-for="option in lengthOptions" :key="option.key" :value="option.label">
            {{ option.label }}
          </option>
        </select>
      </div>
      
      <!-- 风格选择 -->
      <div class="option-group">
        <select v-model="form.style" class="option-select">
          <option v-for="(style, key) in styleOptions" :key="key" :value="style.name">
            {{ style.name }}
          </option>
        </select>
      </div>
      
      <!-- 文件上传按钮 - 仅在文案改进和总结报告显示 -->
      <div class="upload-buttons" v-if="textType === 'improve' || textType === 'summary'">
        <input type="file" ref="fileInput" accept=".doc,.docx,.pdf,.txt,.md" style="display: none" @change="handleFileUpload">
        <button 
          class="upload-btn" 
          @click="triggerFileUpload"
          :title="form.documentName ? `已上传: ${form.documentName}` : '上传参考文档'"
        >
          📄
        </button>
        <div v-if="form.documentName" class="uploaded-filename">
          <span>{{ form.documentName }}</span>
          <button class="remove-filename" @click="removeDocument">×</button>
        </div>
      </div>
      
      <!-- 生成按钮 -->
      <button class="generate-btn" @click="generateText" :disabled="isGenerating">
        <span v-if="!isGenerating">生成文案</span>
        <span v-else>生成中...</span>
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import appCache from '../../utils/appCache'
export default {
  name: 'TextToolbar',
  props: {
    params: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      textType: 'general',
      isGenerating: false,
      isCollapsed: false,
      form: {
        prompt: '',
        model: '',
        wordCount: '',
        style: '',
        documentName: '',
        documentUrl: ''
      },
      models: {},
      lengthOptions: [
        { key: 'short', label: '短文案（50~100）', min: 50, max: 100 },
        { key: 'medium', label: '中文案（100~500）', min: 100, max: 500 },
        { key: 'long', label: '长文案（500以上）', min: 500, max: 9999 }
      ],
      styleOptions: {},
      hasAutoGenerated: false
    }
  },
  computed: {
    textModels() {
      return Object.entries(this.models).map(([identifier, model]) => ({
        ...model,
        identifier
      }))
    }
  },
  watch: {
    params: {
      handler(newParams) {
        if (newParams && Object.keys(newParams).length > 0) {
          this.handleParams(newParams)
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      console.log('🚀 TextToolbar 初始化开始')
      
      // 先加载配置和模型
      await Promise.all([
        this.loadTextConfig(),
        this.loadModels()
      ])
      
      console.log('🚀 初始化完成，styleOptions:', this.styleOptions)
    },
    
    async handleParams(params) {
      console.log('=== TextToolbar 处理参数 ===', params)
      
      if (params.type) {
        this.textType = params.type
      }
      if (params.prompt) {
        this.form.prompt = params.prompt
      }
      if (params.model) {
        this.form.model = params.model
      }
      if (params.wordCount) {
        const lengthOpt = this.lengthOptions.find(opt => opt.key === params.wordCount)
        this.form.wordCount = lengthOpt ? lengthOpt.label : params.wordCount
      }
      if (params.style) {
        this.form.style = params.style
      }
      if (params.documentUrl) {
        this.form.documentUrl = params.documentUrl
      }
      if (params.documentName) {
        this.form.documentName = params.documentName
      }
      
      await this.waitForLoadComplete()
      
      if (!this.hasAutoGenerated) {
        this.hasAutoGenerated = true
        setTimeout(() => {
          this.generateText()
        }, 500)
      }
    },
    
    async waitForLoadComplete() {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (Object.keys(this.models).length > 0 && Object.keys(this.styleOptions).length > 0) {
            clearInterval(checkInterval)
            resolve()
          }
        }, 100)
        setTimeout(() => {
          clearInterval(checkInterval)
          resolve()
        }, 3000)
      })
    },
    
    async loadTextConfig() {
      try {
        console.log('📋 开始加载文案配置...')
        const data = await appCache.getTextConfig()
        console.log('📋 收到配置数据:', data)
        
        if (data) {
          this.lengthOptions = data.lengthOptions || this.lengthOptions
          this.styleOptions = data.styles || {}
          
          console.log('📋 lengthOptions:', JSON.stringify(this.lengthOptions, null, 2))
          console.log('📋 styleOptions:', JSON.stringify(this.styleOptions, null, 2))
          
          const styleKeys = Object.keys(this.styleOptions)
          console.log('📋 styleKeys:', styleKeys)
          
          if (styleKeys.length > 0) {
            const firstStyleKey = styleKeys[0]
            console.log('📋 第一个styleKey:', firstStyleKey)
            console.log('📋 第一个样式数据:', this.styleOptions[firstStyleKey])
            
            // 只有当还没有设置值时才使用默认值（避免覆盖路由参数）
            if (!this.form.style) {
              this.form.style = this.styleOptions[firstStyleKey].name
              console.log('📋 设置默认样式:', this.form.style)
            }
            if (!this.form.wordCount) {
              this.form.wordCount = this.lengthOptions[1]?.label || '中文案（100~500）'
              console.log('📋 设置默认字数:', this.form.wordCount)
            }
          } else {
            console.warn('⚠️ styleOptions 为空！')
          }
          
          // 处理默认模型
          if (data.model && !this.form.model) {
            if (data.model === 'default') {
              // 如果是默认模型，选择第一个可用的文案模型
              const textModels = Object.values(this.models)
              if (textModels.length > 0) {
                this.form.model = Object.keys(this.models)[0]
              }
            } else {
              // 否则使用配置中的模型
              this.form.model = data.model
            }
          }
        } else {
          console.warn('⚠️ 配置数据为空！')
        }
        
        console.log('📋 配置加载完成，当前form:', JSON.stringify(this.form, null, 2))
      } catch (error) {
        console.error('❌ 加载文案配置失败:', error)
      }
    },

    async loadModels() {
      try {
        const textModels = await appCache.getModelsByCategory('text')
        this.models = {}
        textModels.forEach(model => {
          this.models[model.identifier] = {
            name: model.name,
            description: model.description,
            type: model.model_type,
            image_url: model.image_url,
            is_new: model.is_new,
            is_hot: model.is_hot,
            discount: model.discount
          }
        })
        // 过滤出文案模型并设置默认值
        if (Object.keys(this.models).length > 0) {
          // 只有当form.model为空时才设置默认值
          if (!this.form.model) {
            this.form.model = Object.keys(this.models)[0]
          }
        }
      } catch (error) {
        console.error('加载模型失败:', error)
      }
    },

    triggerFileUpload() {
      this.$refs.fileInput.click()
    },
    
    async handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      
      this.form.documentName = file.name
      
      try {
        const formData = new FormData()
        formData.append('file', file)
        
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        if (userInfo && userInfo.id) {
          formData.append('user_id', userInfo.id)
        }
        
        const response = await axios.post('/api/document-upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        if (response.data.code === 200) {
          this.form.documentUrl = response.data.data.document_url
          alert('文档上传成功！')
        } else {
          alert('文档上传失败: ' + (response.data.message || '未知错误'))
          this.removeDocument()
        }
      } catch (error) {
        console.error('文档上传失败:', error)
        alert('文档上传失败，请重试')
        this.removeDocument()
      }
    },
    
    removeDocument() {
      this.form.documentName = ''
      this.form.documentUrl = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    async generateText() {
      if (!this.form.prompt.trim()) {
        alert('请输入提示词')
        return
      }
      
      this.isGenerating = true
      
      // 平滑滚动到顶部，让用户看到生成过程
      window.scrollTo({ top: 0, behavior: 'smooth' })
      
      let textTypeName = '通用文案'
      if (this.textType === 'ecommerce') {
        textTypeName = '电商营销'
      } else if (this.textType === 'project') {
        textTypeName = '项目方案'
      } else if (this.textType === 'improve') {
        textTypeName = '文案改进'
      } else if (this.textType === 'summary') {
        textTypeName = '总结报告'
      }
      
      const loadingItem = {
        id: Date.now(),
        type: textTypeName,
        prompt: this.form.prompt,
        model: this.form.model,
        wordCount: this.form.wordCount,
        style: this.form.style,
        content: '',
        generateTime: this.getCurrentTime(),
        documentUrl: this.form.documentUrl,
        documentName: this.form.documentName,
        isLoading: true,
        progress: 0,
        statusText: '正在连接AI...'
      }
      
      this.$emit('add-text', loadingItem)
      
      const updateProgress = () => {
        if (loadingItem.progress < 90) {
          const newProgress = loadingItem.progress + Math.random() * 15
          const cappedProgress = Math.min(newProgress, 90)
          
          let statusText = '正在生成文案...'
          if (cappedProgress < 30) {
            statusText = '正在连接AI...'
          } else if (cappedProgress < 60) {
            statusText = '正在分析提示词...'
          }
          
          loadingItem.progress = cappedProgress
          loadingItem.statusText = statusText
          
          this.$emit('update-progress', loadingItem)
        }
      }
      
      const progressInterval = setInterval(updateProgress, 200)
      
      try {
        let mergedPrompt = this.form.prompt
        
        if (this.form.wordCount) {
          mergedPrompt += `，字数要求：${this.form.wordCount}`
        }
        
        if (this.form.style) {
          mergedPrompt += `，风格：${this.form.style}`
        }
        
        if (textTypeName) {
          mergedPrompt += `，类型：${textTypeName}`
        }
        
        // 获取用户 ID
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        const userId = userInfo.id || 0
        
        const requestData = {
          topic: mergedPrompt,
          keywords: '',
          type: this.textType,
          style: this.form.style,
          length: this.form.wordCount,
          model: this.form.model,
          user_id: userId
        }
        
        if (this.form.documentUrl) {
          requestData.document_url = this.form.documentUrl
          requestData.document_name = this.form.documentName
        }
        
        console.log('📤 发送请求到 /api/text-generate')
        console.log('📋 请求参数:', requestData)
        
        const response = await fetch('/api/text-generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        })
        
        console.log('📥 收到响应，状态码:', response.status)
        
        // 先获取响应数据
        const data = await response.json()
        console.log('📦 响应数据:', data)
        this.isGenerating = false
        
        clearInterval(progressInterval)
        
        // 更新进度到 100%
        loadingItem.progress = 100
        loadingItem.statusText = '生成完成！'
        this.$emit('update-progress', loadingItem)
        
        // 延迟一下让进度条完成
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // 检查是否返回失败状态
        if (data.failed) {
          loadingItem.content = '生成失败，请尝试重新提交'
          loadingItem.isLoading = false
          loadingItem.progress = 0
          this.$emit('update-progress', loadingItem)
          return
        }
        
        // 更新为完成状态
        if (data.code === 200) {
          loadingItem.content = data.content
          loadingItem.isLoading = false
          loadingItem.progress = 0
          console.log('✅ 生成成功')
        } else {
          loadingItem.content = '生成失败: ' + (data.message || '未知错误')
          loadingItem.isLoading = false
          loadingItem.progress = 0
          console.log('❌ 生成失败:', data.message)
        }
        this.$emit('update-progress', loadingItem)
      } catch (error) {
        clearInterval(progressInterval)
        this.isGenerating = false
        loadingItem.content = '生成失败，请重试'
        loadingItem.isLoading = false
        loadingItem.progress = 0
        this.$emit('update-progress', loadingItem)
        console.error('❌ 异常:', error)
      }
    },
    
    getCurrentTime() {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed
    },
    changeTextType(type) {
      this.textType = type
    }
  }
}
</script>

<style scoped>
.toolbar-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: all 0.3s ease;
}

/* 工具栏头部 */
.toolbar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 60px;
  position: relative;
}

/* 顶部按钮栏 */
.toolbar-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* 收缩/展开按钮 */
.collapse-btn {
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #4ade80;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.collapse-btn.expanded {
  color: #ff4757;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.collapse-btn:hover {
  transform: translate(-50%, -50%) scale(1.1);
}

.collapse-btn.expanded:hover {
  transform: translate(-50%, -50%) scale(1.1);
}

.collapse-arrow {
  transition: transform 0.3s ease, color 0.3s ease;
  font-size: 20px;
  font-weight: bold;
}

.collapse-arrow.up {
  transform: rotate(180deg);
}

/* 收缩状态 */
.toolbar-wrapper.collapsed {
  height: 60px;
  overflow: visible;
}

.toolbar-wrapper.collapsed .prompt-input-container,
.toolbar-wrapper.collapsed .toolbar-options {
  display: none;
}

.toolbar-wrapper.collapsed .toolbar-header {
  align-items: center;
  justify-content: center;
}

/* 上传按钮 */
.upload-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.upload-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(0, 230, 172, 0.2);
  border: 1px solid rgba(0, 230, 172, 0.4);
  color: #00e6ac;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.upload-btn:hover {
  background: rgba(0, 230, 172, 0.3);
  transform: scale(1.05);
}

.uploaded-filename {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
}

.uploaded-filename span {
  color: #fff;
  font-size: 13px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-filename {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.4);
  color: #ff6b6b;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.remove-filename:hover {
  background: rgba(255, 107, 107, 0.3);
}

/* 底部展开按钮 */
.expand-btn {
  position: fixed;
  bottom: 20px;
  right: 50%;
  transform: translateX(50%);
  background: #ff4757;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.4);
  z-index: 1000;
}

.expand-btn:hover {
  transform: translateX(50%) scale(1.1);
  box-shadow: 0 6px 16px rgba(255, 71, 87, 0.6);
}

.expand-arrow {
  color: white;
  font-size: 24px;
  font-weight: bold;
  transition: transform 0.3s ease;
}

.toolbar-btn {
  background: rgba(255, 255, 255, 0.1);
  color: #e6f1ff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.toolbar-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.toolbar-btn.active {
  background: #00e6ac;
  color: #000;
  border-color: #00e6ac;
  font-weight: 600;
}

/* 提示词输入区 */
.prompt-input-container {
  width: 100%;
}

.prompt-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  color: #e6f1ff;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  min-height: 80px;
}

.prompt-input::placeholder {
  color: #8892b0;
}

/* 底部参数选项 */
.toolbar-options {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.option-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.option-select {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 8px 12px;
  color: #e6f1ff;
  font-size: 14px;
  min-width: 100px;
  cursor: pointer;
}

.option-select option {
  background: #0a192f;
  color: #e6f1ff;
}

/* 生成按钮 */
.generate-btn {
  background: #00e6ac;
  color: #000;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: auto;
  white-space: nowrap;
}

.generate-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 230, 172, 0.3);
}

.generate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

@media (max-width: 768px) {
  .toolbar-options {
    flex-direction: column;
    align-items: stretch;
  }
  
  .generate-btn {
    margin-left: 0;
  }
}
</style>
